import { useContext, useState } from 'react';
import { Description, ExpandMore } from '@mui/icons-material';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  FormControl,
  Box,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { SMBSelect } from '../../../components';
import { createPlanBrochure, getAgentsSimple } from '../../../fetch';
import { LoadingButton } from '@mui/lab';
import {
  ICladdingVersions,
  IElevationDropdownResponse,
  IPeopleSimple,
  IPlanDropdownResponse,
  IResponse
} from '../../../models';
import { useSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import { useQuery } from 'react-query';
import { downloadFile, getDownloadFileName } from '../../../helpers';
import { SalesPlansDropdown } from '../../../components/dropdowns/sales-plans-dropdown';
import {
  getSalesCladdingVersions,
  getSalesLookupAvailableElevations,
  getSalesLookupPlans
} from '../../../fetch/lookups/sales-portal';
import { UserContext } from '../../../context';

export const PlanBrochure = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingElevations, setIsLoadingElevations] = useState<boolean>(false);
  const [elevationList, setElevationList] = useState<IElevationDropdownResponse[]>();
  const [isGeneratingBrochure, setIsGeneratingBrochure] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<any>();

  const getCladdingNiceName = val => {
    const claddingItem = claddingVersions?.filter(cladding => cladding.value === String(val));
    return claddingItem?.map(c => c.description);
  };

  const { isLoading: isLoadingAgents, data: agentsData } = useQuery<
    IResponse<IPeopleSimple[]>,
    Error
  >(['agents'], () => getAgentsSimple());

  const { isLoading: isLoadingCladdingVersions, data: claddingVersions } = useQuery<
    ICladdingVersions[],
    Error
  >(['claddingVersions'], () => getSalesCladdingVersions());

  const { isLoading: isLoadingPlans, data: plans } = useQuery<IPlanDropdownResponse[], Error>(
    ['getSalesLookupPlansPlanBrochure'],
    () => getSalesLookupPlans({ showArchived: false })
  );

  const { user } = useContext(UserContext);
  const isAgent = user?.userRoles?.includes('BL_Agent');
  const agentId = user?.externalUser?.personId;

  const fetchElevationsList = async val => {
    try {
      setIsLoadingElevations(true);
      const res = await getSalesLookupAvailableElevations(val);
      setElevationList(res);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error loading Elevations, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoadingElevations(false);
    }
  };

  return (
    <>
      <Accordion className={classes.root}>
        <AccordionSummary
          className={classes.accordion}
          expandIcon={<ExpandMore />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <div className={classes.topPanelSummaryWrapper}>
            <Typography variant="h6">Plan Brochure</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <div className={classes.panelSummaryWrapper}>
            <Box mt={1}>
              <Formik
                enableReinitialize={true}
                initialValues={{
                  agents: isAgent ? agentId : '',
                  plans: '',
                  elevations: '',
                  cladding: '',
                  threeCarFootages: false
                }}
                onSubmit={async (values, actions) => {
                  try {
                    setIsGeneratingBrochure(true);
                    const res = await createPlanBrochure(values.plans, {
                      planElevationId: Number(values.elevations),
                      agentId: Number(values.agents),
                      cladding: values.cladding,
                      useThreeCar: values.threeCarFootages
                    });
                    downloadFile(res.data, getDownloadFileName(res) ?? 'plan-brochure.pdf');
                  } catch (error) {
                    enqueueSnackbar(error?.Detail || 'Cannot generate plan brochure.', {
                      variant: 'error'
                    });
                    console.log(error);
                  } finally {
                    setIsGeneratingBrochure(false);
                  }
                }}
              >
                {({
                  isSubmitting,
                  values,
                  initialValues,
                  setFieldValue,
                  handleSubmit,
                  dirty,
                  isValid,
                  handleBlur,
                  errors,
                  touched
                }) => {
                  return (
                    <>
                      <Form onSubmit={handleSubmit} autoComplete="none">
                        <Grid container spacing={1} flexWrap={{ xs: 'wrap', xl: 'nowrap' }}>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <SMBSelect
                              disabled={isLoadingAgents || isAgent}
                              name={`agents`}
                              label={`Agent`}
                              variant="outlined"
                              handleClear={() => {
                                setFieldValue('agents', '');
                                setFieldValue('plans', '');
                                setFieldValue('elevations', '');
                                setFieldValue('cladding', '');
                                setFieldValue('threeCarFootages', false);
                              }}
                              value={values.agents || ''}
                              size="small"
                              onChange={e => {
                                setFieldValue('agents', Number(e.target.value));
                                setFieldValue('threeCarFootages', false);
                              }}
                            >
                              {!isLoadingAgents &&
                                agentsData?.records.map((record, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={record.personId}>
                                      {record.lastName}, {record.firstName}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <SalesPlansDropdown
                              isRequired={true}
                              isDisabled={isLoadingPlans || !values.agents}
                              value={values.plans}
                              handleChange={val => {
                                setFieldValue('plans', val);
                                setFieldValue('elevations', '');
                                setFieldValue('cladding', '');
                                setFieldValue('threeCarFootages', false);
                                if (val) {
                                  fetchElevationsList(val);
                                }
                                setSelectedPlan(plans?.filter(plan => Number(plan?.value) === val));
                              }}
                              isEnabled={!values.agents}
                              handleClear={() => {
                                setFieldValue('plans', '');
                                setFieldValue('elevations', '');
                                setFieldValue('cladding', '');
                                setFieldValue('threeCarFootages', false);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <FormControl fullWidth variant="outlined" size="medium" required>
                              <SMBSelect
                                disabled={!values.plans || isLoadingElevations}
                                label={'Elevation'}
                                name="elevations"
                                value={values.elevations}
                                variant="outlined"
                                required
                                onChange={e => {
                                  setFieldValue('elevations', e.target.value);
                                  if (
                                    !elevationList?.find(list => list?.value === values?.elevations)
                                      ?.hasGarage
                                  ) {
                                    setFieldValue('threeCarFootages', false);
                                  }
                                }}
                                SelectProps={{
                                  MenuProps: {
                                    sx: {
                                      maxHeight: '30rem'
                                    }
                                  }
                                }}
                              >
                                {(!elevationList || elevationList.length) === 0 && (
                                  <MenuItem disabled>No Elevations to list</MenuItem>
                                )}
                                {elevationList &&
                                  elevationList.map((elevation, index) => {
                                    return (
                                      <MenuItem key={`${index}`} value={elevation.value}>
                                        {elevation.description}
                                      </MenuItem>
                                    );
                                  })}
                              </SMBSelect>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <SMBSelect
                              disabled={isLoadingCladdingVersions || !values.elevations}
                              name={`cladding`}
                              label="Cladding"
                              variant="outlined"
                              required
                              id={`cladding`}
                              value={values.cladding}
                              size="small"
                              onChange={e => {
                                setFieldValue('cladding', String(e.target.value));
                              }}
                            >
                              {selectedPlan &&
                                selectedPlan?.[0]?.availableCladdings?.map((cladding, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={cladding}>
                                      {getCladdingNiceName(cladding)}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormControl component="fieldset" size="small" fullWidth>
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={values.threeCarFootages}
                                      name="threeCarFootages"
                                      onChange={e => {
                                        e.target.checked
                                          ? setFieldValue('threeCarFootages', true)
                                          : setFieldValue('threeCarFootages', false);
                                      }}
                                      disabled={
                                        !values.agents ||
                                        !elevationList?.find(
                                          list => list?.value === values?.elevations
                                        )?.hasGarage
                                      }
                                    />
                                  }
                                  label="Use 3 Car Footages"
                                />
                              </FormGroup>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={'auto'}>
                            <LoadingButton
                              id="generate-plan-brochure"
                              type="submit"
                              color="primary"
                              variant="contained"
                              disabled={isGeneratingBrochure || !values.cladding}
                              loading={isGeneratingBrochure}
                              startIcon={<Description />}
                              className={classes.generateBtn}
                            >
                              Generate & Download
                            </LoadingButton>
                          </Grid>
                        </Grid>
                      </Form>
                    </>
                  );
                }}
              </Formik>
            </Box>
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  generateBtn: {
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  }
}));
